import React from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { AdminEdmLiteral } from "../../../types/adminEdm";
import workspaceTheme from "../../../components/artwork-workspace/workspaceTheme.module.css";
import { createEmptyEdmLiteral } from "./edm-authoring.shared";
import { EdmFieldLabel } from "./EdmFieldLabel";

type Props = {
  label: React.ReactNode;
  helpText?: string;
  labelHelpText?: React.ReactNode;
  property: string;
  values: AdminEdmLiteral[];
  disabled?: boolean;
  defaultLanguage?: string;
  footer?: React.ReactNode;
  onChange: (next: AdminEdmLiteral[]) => void;
};

export const EdmLiteralListEditor = ({
  label,
  helpText,
  labelHelpText,
  property,
  values,
  disabled = false,
  defaultLanguage = "en",
  footer,
  onChange,
}: Props) => {
  const patchValue = (index: number, patch: Partial<AdminEdmLiteral>) => {
    onChange(values.map((value, valueIndex) => (
      valueIndex === index ? { ...value, ...patch } : value
    )));
  };

  const addValue = () => {
    onChange([...values, createEmptyEdmLiteral(property, defaultLanguage)]);
  };

  const removeValue = (index: number) => {
    onChange(values.filter((_, valueIndex) => valueIndex !== index));
  };

  return (
    <div
      className="border rounded p-3 h-100"
      role="group"
      aria-label={typeof label === "string" ? `${label} values` : `${property} values`}
    >
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div>
          <EdmFieldLabel
            as="text"
            label={label}
            helpText={labelHelpText}
            ariaLabel={typeof label === "string" ? `${label} help` : "Literal field help"}
          />
          {helpText ? <div className="text-white-50 small">{helpText}</div> : null}
        </div>
        <Button
          variant="light"
          size="sm"
          type="button"
          className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
          onClick={addValue}
          disabled={disabled}
        >
          Add value
        </Button>
      </div>
      {values.length === 0 ? (
        <div className="text-white-50 small">No values added yet.</div>
      ) : null}
      <div className="d-flex flex-column gap-3">
        {values.map((value, index) => (
          <div key={`${property}-${index}`} className="border rounded p-3 bg-black bg-opacity-10">
            <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
              <div className="small text-white-50">Entry #{index + 1}</div>
              <Button
                variant="light"
                size="sm"
                type="button"
                className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSmall} ${workspaceTheme.cardDangerAction}`}
                disabled={disabled}
                onClick={() => removeValue(index)}
              >
                Remove
              </Button>
            </div>
            <Row className="g-3 align-items-start">
              <Col lg={property === "dc:description" ? 9 : 8}>
                <Form.Label className="small text-white-50 mb-1">Value</Form.Label>
                {property === "dc:description" ? (
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={value.value || ""}
                    disabled={disabled}
                    onChange={(event) => patchValue(index, { value: event.target.value })}
                  />
                ) : (
                  <Form.Control
                    value={value.value || ""}
                    disabled={disabled}
                    onChange={(event) => patchValue(index, { value: event.target.value })}
                  />
                )}
              </Col>
              <Col lg={property === "dc:description" ? 3 : 4}>
                <Form.Label className="small text-white-50 mb-1">Language</Form.Label>
                <Form.Control
                  value={value.lang || ""}
                  placeholder="en, fr, ro"
                  disabled={disabled}
                  onChange={(event) => patchValue(index, { lang: event.target.value })}
                />
              </Col>
            </Row>
          </div>
        ))}
      </div>
      {footer ? <div className="mt-3 pt-3 border-top">{footer}</div> : null}
    </div>
  );
};

export default EdmLiteralListEditor;
